import { Fragment, useEffect, useState } from "react";
import {
	formatInstanceSize,
	getEnvironmentInstanceSizes,
	type EnvironmentInstanceSizeInfo,
} from "@/lib/api";
import { formatCpuRange, formatMemory } from "@/lib/format-k8s-quantity";
import { useBillingRates } from "@/lib/use-billing-rates";
import { formatRateCredits, groupRatesByCategory, groupRatesByModelTab } from "@/lib/billing";
import { DocParagraph, DocRateTable, DocScrollTable, DocSubheading, DocTabs } from "./DocElements";

/**
 * 料金ページの単価。active が true になるたびに API から取り直す。
 * 単価そのものは API から取るので、descriptions (カテゴリの ID ごとの、何に対する料金かの説明) は Markdown に書く。
 */
export function BillingRatesSection({
	active,
	descriptions,
}: {
	active: boolean;
	descriptions: Readonly<Record<string, React.ReactNode>>;
}) {
	const { rates, error } = useBillingRates(active);

	return (
		<>
			{error !== null && <DocParagraph>{error}</DocParagraph>}
			{error === null && rates === null && <DocParagraph>読み込み中…</DocParagraph>}
			{rates !== null &&
				groupRatesByCategory(rates).map(({ category, groups }) => (
					<Fragment key={category.id}>
						<DocSubheading>{category.label}</DocSubheading>
						{descriptions[category.id]}
						{category.id === "modelApi" ? (
							<DocTabs
								tabs={groupRatesByModelTab(groups).map((tab) => ({
									key: tab.key,
									label: tab.label,
									content: <DocRateTable groups={tab.groups} />,
								}))}
							/>
						) : category.id === "environmentServe" ? (
							<EnvironmentServeRates />
						) : (
							<DocRateTable groups={groups} />
						)}
					</Fragment>
				))}
		</>
	);
}

/**
 * サーバー基本料金。単価はサイズの数だけ行があるので、サイズ選択と同じ
 * /api/environments/instance-sizes (単価は計上と同じ規則で解決済み) から GPU の構成と並べた表にする。
 */
function EnvironmentServeRates() {
	const [sizes, setSizes] = useState<EnvironmentInstanceSizeInfo[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const controller = new AbortController();
		getEnvironmentInstanceSizes(controller.signal)
			.then(setSizes)
			.catch((err: Error) => {
				if (controller.signal.aborted) return;
				console.error("サーバー基本料金の単価の取得に失敗しました", err);
				setError(err.message);
			});
		return () => controller.abort();
	}, []);

	return (
		<>
			{error !== null && <DocParagraph>{error}</DocParagraph>}
			{error === null && sizes === null && <DocParagraph>読み込み中…</DocParagraph>}
			{sizes !== null && (
				<DocScrollTable
					columns={[
						{ label: "名前" },
						{ label: "GPU" },
						{ label: "課金（クレジット / 時間）", align: "right" },
					]}
					rows={sizes
						.filter((info) => !info.deviceOnly)
						.map((info) => ({
							key: info.size,
							cells: [
								`${formatInstanceSize(info.size)}（${formatCpuRange(info.cpuRequest, info.cpuLimit)} / ${formatMemory(info.memory)}）`,
								info.gpu === null
									? "なし"
									: `${info.gpu.name} ×${info.gpu.count}（${info.gpu.totalMemoryGb} GB）`,
								formatRateCredits(info.creditsPerHour),
							],
						}))}
				/>
			)}
		</>
	);
}
